<template>
  <v-card flat>
    <v-col cols="12" class="px-0">
      <v-btn color="primary" icon @click="beforeLeave">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.video.colors') }}
      </span>
    </v-col>

    <v-row no-gutters class="py-0 px-2">
      <v-col cols="12">
        <v-card flat>
          <span>
            {{ $t('message.pages.video.messageAppearance') }}
          </span>
        </v-card>
      </v-col>

      <v-col cols="12" class="mt-7 mb-4">
        <v-row>
          <v-col cols="3" class="pb-0 mr-n7">
            <p class="mb-0">
              {{ $t('message.playerPage.primaryColor') }}
            </p>
          </v-col>
          <v-col cols="2">
            <v-menu
              :disabled="!hasEditVideoPerm"
              offset-x
              v-model="menuPrimaryColor"
              top
              nudge-bottom="105"
              nudge-left="16"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :color="primaryColor"
                  :style="setPrimaryColor"
                  v-on="on"
                  icon
                  elevation="3"
                ></v-btn>
              </template>
              <v-color-picker
                elevation="3"
                v-model="primaryColor"
                flat
                mode="hexa"
                hide-mode-switch
                :maxlength="max"
                @input="changePlayer()"
              ></v-color-picker>
            </v-menu>
          </v-col>
          <v-col cols="4" class="pb-0">
            <p class="mb-0">
              {{ $t('message.playerPage.controlBarColor') }}
            </p>
          </v-col>
          <v-col cols="2">
            <v-menu
              :disabled="!hasEditVideoPerm"
              offset-x
              v-model="menuControlsColor"
              top
              nudge-bottom="105"
              nudge-left="16"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :color="controlsColor"
                  v-on="on"
                  :style="setControlsColor"
                  icon
                  elevation="3"
                ></v-btn>
              </template>
              <v-color-picker
                elevation="3"
                v-model="controlsColor"
                flat
                mode="hexa"
                hide-mode-switch
                :maxlength="max"
                @input="changePlayer()"
              ></v-color-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <span
      v-if="internalError"
      class="red--text caption align-text ml-2 mt-2 d-block"
    >
      <v-icon color="red" size="16" class="mt-n1"> mdi-alert-circle </v-icon>
      {{ $t('message.pages.video.errors.addColors') }}
    </span>

    <v-row no-gutters v-if="hasEditVideoPerm">
      <v-col cols="12">
        <v-card-actions class="px-8 pt-4 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            class="mr-2 px-4"
            text
            :disabled="loadingButtonSave || withoutChanges"
            @click="handleUndoChanges"
          >
            {{ $t('message.pages.video.discardChanges') }}
          </v-btn>
          <v-btn
            class="px-6"
            color="primary"
            :disabled="loadingButtonSave || withoutChanges"
            :loading="loadingButtonSave"
            @click="handleUpdateColors"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
        <unsaved-changes-modal />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import UnsavedChangesModal from '../../../components/UnsavedChangesModal.vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import i18n from '@/translations/lang'
import _ from 'lodash'

export default {
  name: 'VideoPlayerColors',
  components: {
    UnsavedChangesModal,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
    liveVideoId: {
      required: false,
    },
    isExternalSourceVideo: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    menuPrimaryColor: false,
    menuControlsColor: false,
    controlsColor: null,
    primaryColor: null,
    max: 7,
    loadingButtonSave: false,
    videoPlayer: {},
    profileId: null,
    profileConfig: {},
    initialConfig: {
      controlsColor: null,
      primaryColor: null,
    },
    withoutChanges: true,
    videoId: '',
  }),
  async mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.profileId = this.$store.state.video.video.config?.profile
    this.profileConfig = this.$store.state.video.profile?.colors
    this.videoPlayer = this.$store.state.video.player.config
    this.controlsColor = this.profileConfig?.controlsColor
    this.primaryColor = this.profileConfig?.primaryColor
    this.initialConfig = _.cloneDeep(this.profile.colors)

    this.$root.$on('discardChanges', () => {
      this.handleUndoChanges()
      this.handleBack()
    })
  },
  watch: {
    checkChanges: {
      handler(hasChanges) {
        this.withoutChanges = !hasChanges
      },
    },
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
      internalError: (state) => state.video.internalError,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
    setPrimaryColor() {
      const { menuPrimaryColor, primaryColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-color-main', primaryColor)
      return {
        backgroundColor: primaryColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuPrimaryColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    setControlsColor() {
      const { controlsColor, menuControlsColor } = this
      let root = document.querySelector(':root')
      root.style.setProperty('--plyr-video-control-color', controlsColor)
      return {
        backgroundColor: controlsColor,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        borderRadius: menuControlsColor ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out',
      }
    },
    checkChanges() {
      const laterPrimaryColor = this.profile.colors.primaryColor
      const laterControlsColor = this.profile.colors.controlsColor

      const upcomingPrimaryColor = this.primaryColor
      const upcomingControlsColor = this.controlsColor

      const isPrimaryColorChanged = laterPrimaryColor !== upcomingPrimaryColor
      const isControlsColorChanged =
        laterControlsColor !== upcomingControlsColor

      return isPrimaryColorChanged || isControlsColorChanged
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',

      'createProfile',

      'setProfileToVideos',
    ]),
    ...mapMutations('video', [
      'UPDATE_VIDEO_PROFILE',
      'UPDATE_VIDEO_PROFILE_ID',
      'UPDATE_STATUS_ERROR',
    ]),
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    async handleUpdateColors() {
      this.loadingButtonSave = true
      try {
        this.profile.colors.controlsColor = this.controlsColor
        this.profile.colors.primaryColor = this.primaryColor
        if (!this.video.config.profile) {
          await this.createProfile(this.videoId)
          this.video.config.profile = this.profile_id
          this.profileId = this.profile_id
          await this.setProfileToVideos([this.profileId, this.videoId])
        }
        await this.updateProfile([this.profileId, this.profile])
        this.handleSnackbar(i18n.t('message.pages.video.changesSaved'))
        this.initialConfig = _.cloneDeep(this.profile.colors)
        this.withoutChanges = true
        this.loadingButtonSave = false
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
      this.$root.$emit('updateEmbedPreviewChange')
    },
    async changePlayer() {
      if (!this.isExternalSourceVideo) {
        const iframe = document.getElementById('panda-player').contentWindow
        iframe.postMessage(
          {
            type: 'colors',
            parameter: {
              colors: {
                primaryColor: this.primaryColor,
                controlsColor: this.controlsColor,
              },
            },
          },
          '*'
        )
      }
      this.UPDATE_STATUS_ERROR(false)
      return
    },
    handleUndoChanges() {
      this.primaryColor = _.cloneDeep(this.initialConfig.primaryColor)
      this.controlsColor = _.cloneDeep(this.initialConfig.controlsColor)
      this.changePlayer()
    },
    beforeLeave() {
      if (this.checkChanges) {
        this.$root.$emit('unsavedChangesModal')
      } else {
        this.UPDATE_STATUS_ERROR(false)
        this.handleBack()
      }
    },
    handleSnackbar(item) {
      if (!item) {
        item = i18n.t('message.changesSaved')
      }
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: item,
      })
    },
  },
}
</script>

<style></style>
